import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ConnectedDevice, ShogunChopResource } from '@localagent/sdk';
import { useAtomValue } from 'jotai';
import { holdsIdentityAtom, tokenAtom } from '../stores/auth/atoms';
import { apiBaseUrlAtom } from '../stores/sdk/atoms';
import { getSDK } from '../services/sdk-instance';

interface ShogunChopContextValue {
  /**
   * True when at least one local device appears in the account's connection
   * table (active_connections). This reflects that a daemon has opened a
   * socket — but NOT that its bridge connection workflow is up, so a device
   * can be `available` while not yet reachable. Use `bridgeConnected` for
   * the authoritative "device-routed work will succeed" signal.
   */
  available: boolean;
  /**
   * True when at least one connected device reports a running, connected
   * bridge ConnectionWorkflow (the workflow-backed `connected` flag). This is
   * the signal that device-routed requests (file tools, MCP, LLM) will
   * actually reach a daemon.
   */
  bridgeConnected: boolean;
  /** Connected devices for the current account, refreshed periodically. */
  connectedDevices: ConnectedDevice[];
  /** Direct ShogunChop resource — only set on platforms that discovered a local URL (Electron). */
  resource: ShogunChopResource | null;
}

export const ShogunChopContext = createContext<ShogunChopContextValue>({
  available: false,
  bridgeConnected: false,
  connectedDevices: [],
  resource: null,
});

const POLL_INTERVAL_MS = 30_000;

function sameDevices(a: ConnectedDevice[], b: ConnectedDevice[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (d, i) =>
      d.device_id === b[i].device_id &&
      d.connected_at === b[i].connected_at &&
      d.last_heartbeat === b[i].last_heartbeat,
  );
}

export function ShogunChopProvider({
  resource,
  children,
}: {
  resource: ShogunChopResource | null;
  children: ReactNode;
}) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const token = useAtomValue(tokenAtom);
  const apiBaseUrl = useAtomValue(apiBaseUrlAtom);
  const [connectedDevices, setConnectedDevices] = useState<ConnectedDevice[]>([]);
  const [bridgeConnected, setBridgeConnected] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !apiBaseUrl || !token) {
      setConnectedDevices([]);
      setBridgeConnected(false);
      return;
    }

    let cancelled = false;
    const refresh = async () => {
      const devices = await getSDK().bridge.listConnectedDevices();
      if (cancelled) return;
      // Identity stability, not a fallback: an unchanged device list must not
      // publish a fresh array, or every consumer of this context re-renders on
      // every tick forever.
      setConnectedDevices((prev) => (sameDevices(prev, devices) ? prev : devices));

      // A device row in active_connections does not guarantee its bridge
      // ConnectionWorkflow is up (it can lag on a cold start). Confirm the
      // workflow-backed `connected` flag so the UI distinguishes "registered"
      // from "actually reachable". A single device's transient status error
      // counts as not-connected for that device without aborting the poll.
      const reachable = await Promise.all(
        devices.map((d) => getSDK().bridge.isConnected(d.device_id).catch(() => false)),
      );
      if (!cancelled) setBridgeConnected(reachable.some(Boolean));
    };

    const tick = () => {
      refresh().catch((err: unknown) => {
        console.error('[ShogunChopProvider] device poll failed', err);
        if (!cancelled) {
          setConnectedDevices((prev) => (prev.length === 0 ? prev : []));
          setBridgeConnected(false);
        }
      });
    };

    tick();
    const interval = setInterval(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [isAuthenticated, apiBaseUrl, token]);

  const value = useMemo<ShogunChopContextValue>(
    () => ({
      // A browser tab is a connection, not a ShogunChop install (account ADR-036).
      available: connectedDevices.some((device) => device.kind === 'desktop'),
      bridgeConnected,
      connectedDevices,
      resource,
    }),
    [connectedDevices, bridgeConnected, resource],
  );

  return <ShogunChopContext.Provider value={value}>{children}</ShogunChopContext.Provider>;
}

export function useShogunchop() {
  return useContext(ShogunChopContext);
}
